import { useEffect, useRef } from 'react'
import { Track, RoomEvent, type Participant, type Room } from 'livekit-client'
import { useLivekitStore } from '../../stores/livekitStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { useAuthStore } from '../../stores/authStore'
import { useVoiceStore } from '../../stores/voiceStore'
import {
  getSharedAudioContext,
  resolveAudioOutputSink,
  getPlaybackReferenceNode,
  ensureLimiterWorklet,
  isLimiterWorkletReady,
} from './audioContext'
import { setPeerLoudness, clearPeerLoudness } from '../../services/peerLoudness'

interface AudioPlayerProps {
  participant: Participant
  room: Room
}

/**
 * Plays one remote participant's microphone via the Web Audio graph
 * (MediaStreamAudioSourceNode → GainNode → AudioContext.destination).
 *
 * Why not <audio srcObject>? Chromium has a long-standing bug where a
 * mono mic track piped through HTMLMediaElement gets pinned to a single
 * stereo channel after track-lifecycle events (mute/unmute, replaceTrack).
 * The user reports it as "audible only in the left ear after they
 * mute/unmute". Two earlier mitigations (clone-for-analyser, null-then-set
 * srcObject swaps) didn't fully kill it because the bug is at the
 * HTMLMediaElement layer itself.
 *
 * Web Audio sidesteps that entire layer, but a NAIVE source→gain→
 * destination chain is still vulnerable: when the decoded MediaStreamTrack
 * arrives as 2-channel with one channel silent (Chromium WebRTC produces
 * this after replaceTrack / certain mute-unmute cycles, and some Bluetooth
 * communications endpoints + Safari always negotiate 2-channel SDP for the
 * mic), the `'speakers'` upmix at destination is a no-op — the input is
 * already 2-channel, so the silent channel propagates straight to the
 * speaker. That's exactly the symptom in OSPROD-56: "I can hear them in
 * one ear only, the other ear is dead".
 *
 * Fix: force the GainNode to explicit mono with 'speakers' interpretation.
 * Any input — mono, stereo-with-silent-channel, or true stereo — gets
 * deterministically downmixed to 1 channel BEFORE the destination, then
 * upmixed back to both speakers. The 0.5 factor in the 2→1 'speakers'
 * downmix only applies when the source actually had >1 channel (it's a
 * no-op for the common mono mic), and even in the buggy stereo-with-
 * silent-channel case 50% volume in BOTH ears is strictly better than
 * 100% volume in one ear and silence in the other.
 *
 * Volume / mute / output device:
 *   - Volume goes through a per-peer GainNode (gain.value = peerVolume).
 *   - Self-deafen sets the same gain to 0 (cheap, atomic, no HTML mute).
 *   - Output device routing uses AudioContext.setSinkId — supported in
 *     Chromium 110+. On browsers without it the call is a no-op and audio
 *     plays through the OS default, same fallback behaviour we had before.
 */
// (D) Per-peer loudness CEILING (a measured limiter, not an AGC-toward-target).
// We MEASURE each peer's true arriving loudness in dBFS (analyser on the SOURCE,
// pre-compressor — the honest "how loud is this person" number, logged so we can
// SEE who's hot) and ATTENUATE only the peers LOUDER than a ceiling, leaving
// everyone at/below the ceiling completely untouched (gain = 1.0). DOWNWARD-ONLY
// → echo-safe (reducing output never feeds the echo loop; an upward boost did,
// and was reverted 2026-05-21). This is the opposite of the earlier mistake: the
// old code targeted an RMS (0.0722 ≈ -22.8 dBFS) that sat BELOW the ~-20 dBFS
// speaking norm, so "above target" meant EVERYONE and it cut the whole call. A
// CEILING set ABOVE the norm cuts ONLY the genuinely-loud (e.g. a hot mic).
// The ceiling is runtime-tunable (localStorage 'voiceCeilingDbfs') so it can be
// calibrated live, mid-call, from the logged numbers — no redeploy. The cap is
// DEFAULT-ON since 1.1.294A (the "measure first" period is over — prod
// loudnessDbfs on 2026-05-29 showed hot peers arriving up to -2.5 dBFS, ~5-6 dB
// over the pack, e.g. speaker-bleed echo); kill-switch localStorage
// 'voiceNormalize'='false'. The dBFS measurement + '[loudness]' logging always runs.
const AGC_INTERVAL_MS = 200
const AGC_SPEECH_FLOOR = 0.012 // ≈ -38 dBFS — below this is silence/noise; don't adapt
const AGC_MIN_GAIN = 0.35 // ≈ -9 dB — MAX attenuation; cap a loud talker, never MUTE them ("не заглушать")
const AGC_TIME_CONSTANT = 0.5 // setTargetAtTime smoothing, s — anti-pump
const AGC_CEILING_DBFS_DEFAULT = -14 // ceiling: cut only peers LOUDER than this. ABOVE the ~-20 dBFS speaking norm so normal/quiet talkers pass at unity — only loud moments / hot mics get pulled down. DEFAULT-ON since 1.1.294A. Tune live via localStorage 'voiceCeilingDbfs' (lower = bite harder, e.g. '-16'); kill-switch 'voiceNormalize'='false'.

function AudioPlayer({ participant, room }: AudioPlayerProps) {
  const isDeafened = useAuthStore((s) => s.user?.settings?.isDeafened ?? false)
  // OSPROD-78: peerVolumes is keyed by the LiveKit identity, which —
  // after the matching server-side fix in routes/livekit.ts — is the
  // database user id. The slider in VoiceChannelView writes by userId
  // too, so the two keys finally agree. Before that fix the lookup
  // here read username while the slider wrote userId, and the gain
  // node defaulted to 1.0 forever for every participant.
  const peerVolume = useVoiceStore((s) => s.peerVolumes[participant.identity] ?? 1)
  // OSPROD-95: master output volume — applied as a multiplicative
  // gain so the user has a single channel-wide knob. The per-tile
  // slider was removed in 1.1.21A because it broke when participants
  // reordered and the UI was confusing for the common case ("turn
  // everyone down a bit"); per-peer mute via peerVolumes still
  // factors in (default 1.0 = no change).
  const masterVolume = useVoiceStore((s) => s.masterVolume)
  const audioOutputDeviceId = useVoiceStore((s) => s.audioOutputDeviceId)

  // The full per-peer audio sub-graph. We track these in refs so the
  // volume / output-device effects can update existing nodes in place
  // without rebuilding the source on every render.
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const compressorRef = useRef<DynamicsCompressorNode | null>(null)
  // (E) Optional lookahead brickwall limiter (opt-in) — catches start-of-word
  // onset peaks the no-lookahead compressor leaks. localStorage 'voiceLimiter'.
  const limiterRef = useRef<AudioWorkletNode | null>(null)
  const gainRef = useRef<GainNode | null>(null)
  // (D) Per-peer loudness CEILING: `analyserRef` measures this peer's TRUE
  // arriving loudness (tapped on the SOURCE, pre-compressor); `normGainRef`
  // applies a downward-only ceiling — it attenuates ONLY peers louder than the
  // ceiling and leaves everyone else at unity. Sits BEFORE the OSPROD-78
  // compressor so a hot peer enters it already capped. The cap is OPT-IN
  // (localStorage 'voiceNormalize'='true', default off — measure first); the
  // measurement/logging runs regardless. Ceiling via 'voiceCeilingDbfs'.
  const normGainRef = useRef<GainNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  // Remember the MediaStreamTrack id we're currently wired up to, so a
  // TrackSubscribed event for the SAME track is a no-op. Reconnecting
  // the source for the same underlying track is what triggered the old
  // mono-glitch with HTMLMediaElement; cheap to skip here too.
  const lastTrackIdRef = useRef<string | null>(null)

  useEffect(() => {
    const ctx = getSharedAudioContext()
    // Preload the lookahead-limiter worklet module so it's ready the moment the
    // user opts in (localStorage 'voiceLimiter'='true'); harmless if never used.
    ensureLimiterWorklet(ctx)

    const teardownGraph = () => {
      try { sourceRef.current?.disconnect() } catch { /* noop */ }
      try { compressorRef.current?.disconnect() } catch { /* noop */ }
      try { limiterRef.current?.disconnect() } catch { /* noop */ }
      try { normGainRef.current?.disconnect() } catch { /* noop */ }
      try { analyserRef.current?.disconnect() } catch { /* noop */ }
      try { gainRef.current?.disconnect() } catch { /* noop */ }
      sourceRef.current = null
      compressorRef.current = null
      limiterRef.current = null
      normGainRef.current = null
      analyserRef.current = null
      gainRef.current = null
      lastTrackIdRef.current = null
      clearPeerLoudness(participant.identity)
    }

    const buildGraphFor = (track: MediaStreamTrack) => {
      // If we're already wired to this exact track, nothing to do.
      if (lastTrackIdRef.current === track.id) return
      teardownGraph()

      const stream = new MediaStream([track])
      const source = ctx.createMediaStreamSource(stream)

      // OSPROD-78: per-peer dynamics compressor. Acts as both a
      // soft auto-leveler (a quiet talker on a low-gain headset
      // gets pulled up by ~6–10 dB at the threshold knee) AND a
      // limiter (a hot mic that clips at 0 dBFS gets squashed
      // before the gain stage, so the sum at ctx.destination
      // doesn't blow the user's ears off).
      //
      // Settings rationale:
      //   threshold = -24 dBFS — above ambient room noise but
      //     below typical voice peaks, so normal speech sits
      //     just above the knee.
      //   knee = 3 — tighter knee (was 12): compression engages
      //     decisively right at threshold so the START of a word
      //     is caught, not eased in (the "Aleksei loud on onset"
      //     symptom). Interim — the real onset fix is a lookahead
      //     limiter (this compressor has NO lookahead, so the
      //     first ~1 ms still leaks).
      //   ratio = 12 — strong compression, basically a limiter
      //     above threshold; this is what tames the
      //     "screaming newcomer with hot mic" symptom.
      //   attack = 1 ms / release = 250 ms — faster (was 3 ms) to
      //     bite start-of-word transients; release slow so
      //     consonants don't pump.
      // The user-facing slider stays AFTER the compressor so
      // their manual attenuation is applied to the already-
      // tamed signal — pulling the slider down to 0 still
      // silences the participant entirely, even with the
      // compressor in the path.
      const compressor = ctx.createDynamicsCompressor()
      compressor.threshold.setValueAtTime(-24, ctx.currentTime)
      compressor.knee.setValueAtTime(3, ctx.currentTime) // 12 -> 3: harder knee bites the onset decisively (interim test, pre lookahead-limiter)
      compressor.ratio.setValueAtTime(12, ctx.currentTime)
      compressor.attack.setValueAtTime(0.001, ctx.currentTime) // 3ms -> 1ms: catch the start-of-word transient faster (Aleksei's onset peaks). Still NO lookahead → the proper fix is the lookahead limiter (next).
      compressor.release.setValueAtTime(0.25, ctx.currentTime)

      const gain = ctx.createGain()
      // Force any incoming layout (1ch / 2ch silent / true stereo) down to
      // mono so the destination's 'speakers' upmix puts the signal into
      // BOTH ears. See file-level comment for the OSPROD-56 root cause.
      gain.channelCount = 1
      gain.channelCountMode = 'explicit'
      gain.channelInterpretation = 'speakers'
      // Read fresh state at the moment of build — this effect's closure
      // captures peerVolume/masterVolume/isDeafened from the render where
      // it was created, but `buildGraphFor` may fire ASYNCHRONOUSLY when a
      // TrackSubscribed event arrives much later. Using the closure values
      // means the user's saved masterVolume / current per-peer volume are
      // ignored on first build, and the volume effect below only re-fires
      // if those values CHANGE — so the user had to drag the slider to
      // "wake up" master volume. Pulling fresh values here makes the very
      // first published audio match the user's settings immediately.
      const live = useVoiceStore.getState()
      const livePeer = live.peerVolumes[participant.identity] ?? 1
      const liveMaster = live.masterVolume
      // isDeafened sits in authStore — same fresh-read for the same
      // stale-closure reason.
      const liveDeafened = useAuthStore.getState().user?.settings?.isDeafened ?? false
      gain.gain.value = liveDeafened ? 0 : Math.max(0, livePeer * liveMaster)
      // (D) Loudness CEILING stage. The analyser taps the SOURCE (pre-compressor)
      // to measure each peer's TRUE arriving loudness; `normGain` applies the
      // ceiling attenuation to that same arriving signal BEFORE the compressor,
      // so a hot peer enters the OSPROD-78 compressor already capped and every
      // peer is treated uniformly downstream. Graph:
      //   source → normGain (ceiling) → compressor → gain (slider) → destination
      //   source → analyser (measurement-only leaf, no onward connection)
      const normGain = ctx.createGain()
      normGain.gain.value = 1
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 2048
      source.connect(analyser) // measurement tap: TRUE arriving loudness (pre-everything)
      // (E) Lookahead limiter, right after the source — catches start-of-word
      // ONSET peaks. DEFAULT ON at ceiling -6 dBFS (validated live 2026-05-21:
      // -6 cuts only the genuine over-shoots and leaves normal speech alone). The
      // earlier default-on at -14 was reverted because -14 sat BELOW normal
      // speech peaks (crest factor) and flattened everyone — and back then the
      // ceiling wasn't even applied live (fixed in the per-peer loop below).
      // Kill-switch localStorage 'voiceLimiter'='false'; ceiling live-tunable via
      // 'voiceLimiterCeilingDb' (default -6). When off / module not loaded yet,
      // the source feeds normGain directly (unchanged path).
      let limiterNode: AudioWorkletNode | null = null
      const limiterOn =
        typeof localStorage === 'undefined' || localStorage.getItem('voiceLimiter') !== 'false'
      if (limiterOn && isLimiterWorkletReady(ctx)) {
        try {
          const ceilingDb = (() => {
            const v = parseFloat(localStorage.getItem('voiceLimiterCeilingDb') || '')
            return Number.isFinite(v) ? v : -6
          })()
          limiterNode = new AudioWorkletNode(ctx, 'lookahead-limiter', {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            channelCount: 1,
            processorOptions: { lookaheadMs: 5, attackMs: 1.5, releaseMs: 120, ceilingDb },
          })
        } catch (err) {
          console.warn('[BackgroundAudio] lookahead-limiter node create failed:', err)
          limiterNode = null
        }
      }
      if (limiterNode) {
        source.connect(limiterNode) // source → limiter → normGain → compressor → gain
        limiterNode.connect(normGain)
      } else {
        source.connect(normGain) // ceiling attenuation on the arriving signal (limiter off)
      }
      limiterRef.current = limiterNode
      normGain.connect(compressor) // then the OSPROD-78 compressor (uniform: all enter ≤ ceiling)
      compressor.connect(gain)
      gain.connect(ctx.destination)
      // Also fan this peer's post-gain signal into the shared
      // playback-reference tap. The tap collects every peer's audio
      // into one mix that DFN's AEC stage uses as its NLMS reference
      // (`services/dfn/dfnWorker.ts`). This connection is harmless
      // when AEC is disabled — `MediaStreamAudioDestinationNode`
      // buffers internally with no observable effect on the
      // ctx.destination output. We tap POST-gain (rather than at
      // ctx.destination) so deafen / per-peer volume are already
      // applied — the reference matches what physically leaks into
      // the user's mic from their speakers.
      try {
        gain.connect(getPlaybackReferenceNode())
      } catch (err) {
        // Defensive: if the reference node belongs to a different
        // (closed/stale) context the connect throws. AEC just won't
        // get a reference for this peer until the contexts reconcile.
        console.warn('[BackgroundAudio] playback-reference tap connect failed:', err)
      }
      sourceRef.current = source
      compressorRef.current = compressor
      normGainRef.current = normGain
      analyserRef.current = analyser
      gainRef.current = gain
      lastTrackIdRef.current = track.id

      // AudioContexts start 'suspended' until a user gesture nudges them
      // (browser autoplay policy). The room-join click that put us in
      // this room counts, but be defensive — ignore the rejection if it
      // fires before the gesture is recorded.
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {})
      }
    }

    const updateGraph = () => {
      const audioPub = participant.getTrackPublication(Track.Source.Microphone)
      const track = audioPub?.track?.mediaStreamTrack ?? null
      if (!track) {
        teardownGraph()
        return
      }
      buildGraphFor(track)
    }

    updateGraph()

    // Subscribed / Unsubscribed are the only events that change the
    // identity of the underlying MediaStreamTrack. Muted / Unmuted fire
    // with the same track and would just re-trigger the no-op same-id
    // skip, plus their callback signature is different (publication,
    // participant) and would need a separate handler — not worth the
    // wiring for zero gain.
    // TS resolves room.on(...) to a narrower 2-arg listener overload
    // here, but runtime trackSubscribed / trackUnsubscribed callbacks
    // actually pass (track, publication, participant). Cast the handler
    // so the typecheck passes without changing what arrives at runtime.
    const handle = (_track: any, _pub: any, p: Participant) => {
      if (p.sid === participant.sid) updateGraph()
    }
    room.on(RoomEvent.TrackSubscribed, handle as any)
    room.on(RoomEvent.TrackUnsubscribed, handle as any)
    return () => {
      room.off(RoomEvent.TrackSubscribed, handle as any)
      room.off(RoomEvent.TrackUnsubscribed, handle as any)
      teardownGraph()
    }
    // peerVolume / isDeafened changes are handled by a separate effect
    // below — we don't want every volume tweak to tear down and rebuild
    // the source node (which causes a brief audio drop on Chromium).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant, room])

  // Volume / deafen — drive the existing GainNode in place; no graph
  // rebuild. peerVolumes can exceed 1 (slider boost up to ~2x), and Web
  // Audio honours that — there's no clamp baked into HTMLMediaElement to
  // worry about here.
  useEffect(() => {
    const gain = gainRef.current
    if (!gain) return
    gain.gain.value = isDeafened ? 0 : Math.max(0, peerVolume * masterVolume)
  }, [isDeafened, peerVolume, masterVolume])

  // (D) Per-peer loudness MEASUREMENT + CEILING loop. ALWAYS measures
  // this peer's TRUE arriving loudness (analyser on the source, pre-compressor)
  // and LOGS it in dBFS every ~2 s of speech — that runs regardless of the cap,
  // so we can read real per-person numbers and tune from them.
  // The CEILING is DEFAULT-ON since 1.1.294A (kill-switch localStorage
  // 'voiceNormalize'='false'): it attenuates ONLY peers louder than the ceiling
  // (downward-only, echo-safe), leaving everyone at/below it untouched. Ceiling
  // dBFS: localStorage 'voiceCeilingDbfs' (default AGC_CEILING_DBFS_DEFAULT), read
  // live so it tunes mid-call with no redeploy.
  useEffect(() => {
    const ctx = getSharedAudioContext()
    const buf = new Float32Array(2048)
    let logTick = 0
    let lastLimiterCeiling = Number.NaN
    const readCeilingDbfs = (): number => {
      if (typeof localStorage === 'undefined') return AGC_CEILING_DBFS_DEFAULT
      const v = parseFloat(localStorage.getItem('voiceCeilingDbfs') || '')
      return Number.isFinite(v) ? v : AGC_CEILING_DBFS_DEFAULT
    }
    const id = window.setInterval(() => {
      // Live-tune the lookahead limiter ceiling. The worklet's ceiling is set at
      // graph-build time; here we re-read localStorage 'voiceLimiterCeilingDb'
      // each tick and post changes to the worklet so it can be A/B'd MID-CALL
      // without a rejoin (the worklet applies {ceilingDb} on its port message).
      // Independent of the analyser/normGain path below. NOTE: the limiter's
      // ON/OFF still applies at graph build — toggling voiceLimiter needs a rejoin.
      const limNode = limiterRef.current
      if (limNode && typeof localStorage !== 'undefined') {
        const lv = parseFloat(localStorage.getItem('voiceLimiterCeilingDb') || '')
        const lc = Number.isFinite(lv) ? lv : -6
        if (lc !== lastLimiterCeiling) {
          lastLimiterCeiling = lc
          try { limNode.port.postMessage({ ceilingDb: lc }) } catch { /* noop */ }
        }
      }
      const analyser = analyserRef.current
      const normGain = normGainRef.current
      if (!analyser || !normGain) return
      analyser.getFloatTimeDomainData(buf)
      let sum = 0
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i]
      const rms = Math.sqrt(sum / buf.length)
      // Silence / room noise → don't measure or react; hold the current gain.
      if (rms < AGC_SPEECH_FLOOR) return
      const dbfs = 20 * Math.log10(rms)
      // Stash for the voice-diag collector → ClickHouse (so the measurement
      // lands in the DB, voice_diag_samples.inbound[].loudnessDbfs, not just
      // the console). Keyed by LiveKit identity (= DB user id).
      setPeerLoudness(participant.identity, dbfs)
      const ceilingDbfs = readCeilingDbfs()
      // MEASUREMENT firehose — ALWAYS logs (independent of the cap) every ~2 s of
      // speech so we read real arriving dBFS per peer. Greppable '[loudness]'
      // (VOICE_CRITICAL_INVARIANTS #30 — voice logs are support's triage tool;
      // do not downgrade to console.debug).
      if (logTick++ % 10 === 0) {
        const rel = dbfs - ceilingDbfs
        const verdict = rel > 0 ? `${rel.toFixed(1)} dB OVER ceiling` : `${(-rel).toFixed(1)} dB under ceiling`
        console.info(
          `[loudness] ${participant.identity}: ${dbfs.toFixed(1)} dBFS (${verdict} ${ceilingDbfs})`,
        )
      }
      // CEILING is DEFAULT-ON (kill-switch 'voiceNormalize'='false'). Off → ease the
      // ceiling gain back to unity (user-volume gain is a separate node downstream).
      const ceilingOn =
        typeof localStorage === 'undefined' || localStorage.getItem('voiceNormalize') !== 'false'
      if (!ceilingOn) {
        normGain.gain.setTargetAtTime(1, ctx.currentTime, 0.3)
        return
      }
      // When on: attenuate to the ceiling only when above it; else unity.
      // gain = 10^((ceiling − dbfs)/20), floored at AGC_MIN_GAIN (cap, never mute).
      let desired = 1
      if (dbfs > ceilingDbfs) {
        desired = Math.pow(10, (ceilingDbfs - dbfs) / 20)
        if (desired < AGC_MIN_GAIN) desired = AGC_MIN_GAIN
      }
      normGain.gain.setTargetAtTime(desired, ctx.currentTime, AGC_TIME_CONSTANT)
    }, AGC_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [])

  // Output device routing for the SHARED context. Note: this affects
  // every participant's playback — that's correct behaviour (one OS-level
  // output device for the call, picked once in AudioOutputDeviceSelector).
  // Each AudioPlayer firing the same setSinkId is harmless, the call is
  // idempotent.
  //
  // We resolve the literal `'default'` sink to a concrete deviceId via
  // `resolveAudioOutputSink` — see the helper's comment for the full
  // rationale. Short version: passing `'default'` lets the OS reroute
  // playback when the default-communications device flips (which
  // happens when getUserMedia opens a new mic in eCommunications mode
  // and Windows pairs it with a different output). That caused peer
  // voices to drop to barely-audible after a mic switch.
  useEffect(() => {
    const ctx = getSharedAudioContext() as AudioContext & {
      setSinkId?: (id: string) => Promise<void>
    }
    // Capture the typed function reference up-front; TypeScript can't
    // preserve the `typeof !== 'function'` narrowing across the
    // `await resolveAudioOutputSink(...)` below, and we'd lose the
    // call site without this.
    const setSinkId = ctx.setSinkId
    if (typeof setSinkId !== 'function') return
    let cancelled = false
    void (async () => {
      const target = await resolveAudioOutputSink(audioOutputDeviceId)
      if (cancelled) return
      setSinkId.call(ctx, target).catch((err: unknown) => {
        // Common rejection: the picked deviceId is gone (headset unplugged).
        // Browser falls back to the OS default automatically; just log so
        // we don't surface a confusing toast for a self-healing condition.
        console.warn('[BackgroundAudio] AudioContext.setSinkId failed:', err)
      })
    })()
    return () => {
      cancelled = true
    }
  }, [audioOutputDeviceId])

  return null
}

export default function LiveKitBackgroundAudio() {
  const voiceRoom = useLivekitStore((s) => s.room)
  const voiceRemotes = useLivekitStore((s) => s.remoteParticipants)
  const dmRoom = useDmCallStore((s) => s.room)
  const dmRemotes = useDmCallStore((s) => s.remoteParticipants)

  // Deduplicate by sid in case same participant appears in both rooms
  const seen = new Set<string>()
  const voiceItems = voiceRoom ? voiceRemotes.filter((p) => {
    if (seen.has(p.sid)) return false
    seen.add(p.sid)
    return true
  }).map((p) => ({ key: p.sid, participant: p, room: voiceRoom })) : []

  const dmItems = dmRoom ? dmRemotes.filter((p) => {
    if (seen.has(p.sid)) return false
    seen.add(p.sid)
    return true
  }).map((p) => ({ key: p.sid, participant: p, room: dmRoom })) : []

  return (
    <>
      {voiceItems.map((item) => (
        <AudioPlayer key={item.key} participant={item.participant} room={item.room} />
      ))}
      {dmItems.map((item) => (
        <AudioPlayer key={item.key} participant={item.participant} room={item.room} />
      ))}
    </>
  )
}
